import { ArrowRight, GraduationCap } from "lucide-react";

/**
 * Renders the Young Engineers learning progression.
 * If `highlight` is passed (a Set of stage titles), those stages are emphasised.
 * Default stages are the full pathway from Big Builders → RoboToys.
 */
const FULL_PATHWAY = [
  {
    title: "Big Builders",
    age_range: "Ages 4–5",
    description: "Foundational play that builds curiosity, motor skills, and the love of building.",
  },
  {
    title: "Brick Challenge",
    age_range: "Ages 6–10",
    description: "Moving machines and mechanical models — the start of real engineering thinking.",
  },
  {
    title: "Galileo Technic",
    age_range: "Ages 8–12",
    description: "Advanced mechanical engineering — gears, transmissions, rotational systems.",
  },
  {
    title: "AlgoPlay",
    age_range: "Ages 6–10",
    description: "Hands-on coding with the Algobrix system. Algorithms, logic, debugging.",
  },
  {
    title: "RoboToys",
    age_range: "Ages 9–12",
    description: "Robotics, automation, sensors, and applied programming for real-world challenges.",
  },
];

export default function LearningPathway({
  highlight = new Set(),
  title = "How your child progresses",
  subtitle = "The Young Engineers curriculum is built as a multi-year journey. Start anywhere, progress when ready.",
  stages = FULL_PATHWAY,
}) {
  // Per-stage colour mapping (matches PROGRAM/TIER theme on the backend).
  const TIER_COLORS = {
    "Big Builders": { accent: "#F9C846", secondary: "#FFF6D8" },
    "Brick Challenge": { accent: "#4CAF50", secondary: "#EAF7EA" },
    "Galileo Technic": { accent: "#E53935", secondary: "#FDEAEA" },
    AlgoPlay: { accent: "#00A6B2", secondary: "#E6F7F8" },
    RoboToys: { accent: "#1E88E5", secondary: "#EAF3FE" },
  };
  return (
    <section className="" data-testid="learning-pathway">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-50 text-[#0369A1] shrink-0">
          <GraduationCap className="h-5 w-5" />
        </span>
        <div>
          <p className="eyebrow text-slate-600">Learning pathway</p>
          <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-1 leading-[1.1]">
            {title}
          </h3>
          <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl text-sm sm:text-base">
            {subtitle}
          </p>
        </div>
      </div>

      <ol className="mt-8 grid gap-4 lg:grid-cols-5 sm:grid-cols-2">
        {stages.map((s, i) => {
          const active = highlight.has(s.title);
          const tier = TIER_COLORS[s.title];
          const style = active && tier ? { backgroundColor: tier.accent, borderColor: tier.accent } : {};
          const numberStyle = active && tier ? { backgroundColor: "#FFFFFF", color: tier.accent } : {};
          return (
            <li
              key={s.title}
              className={`relative rounded-2xl border p-5 transition-colors ${
                active ? "text-white" : "bg-white border-slate-200 text-slate-800"
              }`}
              style={style}
              data-testid={`pathway-stage-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                  !active && "bg-slate-100 text-slate-600"
                }`}
                style={numberStyle}
              >
                {i + 1}
              </div>
              <p className="mt-4 font-display font-extrabold">{s.title}</p>
              <p
                className={`mt-1 text-[11px] uppercase tracking-wider font-bold ${
                  active ? "text-white/80" : "text-slate-500"
                }`}
              >
                {s.age_range}
              </p>
              <p
                className={`mt-3 text-xs leading-relaxed ${
                  active ? "text-white/90" : "text-slate-600"
                }`}
              >
                {s.description}
              </p>
              {i < stages.length - 1 && (
                <ArrowRight
                  className={`hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 h-5 w-5 ${
                    active ? "text-white/60" : "text-slate-300"
                  }`}
                />
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export { FULL_PATHWAY };
